import { Formik, FormikHelpers } from 'formik';
import React, { useContext, useState } from 'react';
import styled from 'styled-components/macro';
import { ApiError } from '../../api/api';
import { PrimaryProgressButton } from '../../shared/Button';
import { Form, FormErrorBox } from '../../shared/form/Form';
import { CheckboxField } from '../../shared/form/inputs/CheckboxField';
import { MultiSearchSelect, MultiSelectOptionType } from '../../shared/form/inputs/MultiSearchSelect';
import { FastSelectField } from '../../shared/form/inputs/SelectField';
import { FastTextInputField } from '../../shared/form/inputs/TextInputField';
import { Header3 } from '../../shared/Headers';
import { colourPrimaryOrange } from '../../styling/colours';
import { spacing01, spacing02 } from '../../styling/spacing';
import { DocumentFormModel, documentFormModelValidator } from './documentFormModel';
import { getHandoverPackSectionSelectOptions, getPartMultiSelectOptions } from './documentResponse';
import { HandoverPackSectionContext } from './documents-section/HandoverPackSectionContext';

type Props = {
  initialValues: DocumentFormModel;
  onSubmit: (formModel: DocumentFormModel, formikHelpers: FormikHelpers<DocumentFormModel>) => void;
  error: ApiError | null;
};

export const CreateOrUpdateDocumentForm = ({ initialValues, onSubmit, error }: Props) => {
  const { allDocuments, handoverPackSections, parts } = useContext(HandoverPackSectionContext);
  const [showDuplicateUrlWarning, setShowDuplicateUrlWarning] = useState<boolean>(false);
  const [showDuplicateReferenceWarning, setShowDuplicateReferenceWarning] = useState<boolean>(false);

  const nonInitialDocumentUrls = allDocuments
    .map(document => document.documentUrl.toLowerCase())
    .filter(url => initialValues.documentUrl.toLowerCase() !== url);

  const nonInitialDocumentReferences = allDocuments
    .map(document => document.documentReference.toLowerCase())
    .filter(url => initialValues.documentReference.toLowerCase() !== url);

  const handoverPackSectionOptions = getHandoverPackSectionSelectOptions(handoverPackSections);
  const partOptions = getPartMultiSelectOptions(parts);

  return (
    <>
      <Formik<DocumentFormModel>
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={documentFormModelValidator.validate}
      >
        {formProps => {
          const partIdValues = formProps.values.parts || [];
          const secondaryPartIdValues = formProps.values.secondaryParts || [];
          const showTechnologiesSection = !formProps.values.isAlwaysIncluded;
          const hidePartNumbersSelection = formProps.values.matchAllParts;

          const onPartIdValuesChange = (parts: Array<MultiSelectOptionType<string>>) => {
            formProps.setFieldValue('parts', parts);
          };

          const onSecondaryPartIdValuesChange = (secondaryParts: Array<MultiSelectOptionType<string>>) => {
            formProps.setFieldValue('secondaryParts', secondaryParts);
          };

          const onDocumentUrlBlur = (newValue: string) => {
            if (nonInitialDocumentUrls.includes(newValue.toLowerCase())) {
              setShowDuplicateUrlWarning(true);
            } else {
              setShowDuplicateUrlWarning(false);
            }
          };

          const onDocumentReferenceBlur = (newValue: string) => {
            if (nonInitialDocumentReferences.includes(newValue.toLowerCase())) {
              setShowDuplicateReferenceWarning(true);
            } else {
              setShowDuplicateReferenceWarning(false);
            }
          };

          return (
            <Form>
              <FastTextInputField
                name="documentReference"
                label="Document Reference"
                onBlur={onDocumentReferenceBlur}
              />
              {showDuplicateReferenceWarning && (
                <WarningText>A document with the same reference exists, please double check before saving</WarningText>
              )}
              <FastTextInputField name="documentName" label="Document Name" />
              <FastTextInputField name="customerName" label="Customer Name" />
              <FastTextInputField name="documentUrl" label="Document URL" onBlur={onDocumentUrlBlur} />
              {showDuplicateUrlWarning && (
                <WarningText>A document with the same URL exists, please double check before saving</WarningText>
              )}
              <FastTextInputField name="documentVersion" label="Document Version" />
              <FastSelectField
                name="handoverPackSectionId"
                label="Handover Pack Section"
                options={handoverPackSectionOptions}
              />
              <FastTextInputField name="handoverPackSectionSequence" label="Handover Pack Sequence" />
              <SectionContainer>
                <Header3>Handover Pack Options</Header3>
                <HorizontalDiv>
                  <CheckboxField name="isAlwaysIncluded" label="Is always included?" />
                  <CheckboxField name="includeInContentsPage" label="Include in contents page?" />
                  <CheckboxField name="includeInPrintFolder" label="Include in print folder?" />
                  <CheckboxField name="prePrinted" label="Pre-printed?" />
                  <CheckboxField name="neverPrint" label="Never print" />
                </HorizontalDiv>
              </SectionContainer>
              {showTechnologiesSection && (
                <SectionContainer>
                  <Header3>Technologies</Header3>
                  <HorizontalDiv>
                    <CheckboxField name="useNuHeatSuppliedHeatPump" label="Nu-Heat Supplied Heat Pump" />
                    <CheckboxField name="useSolar" label="Solar" />
                    <CheckboxField name="useUnderfloorHeating" label="Underfloor Heating" />
                    <CheckboxField name="useUserSuppliedHeatPump" label="User Supplied Heat Pump" />
                    <CheckboxField name="useBoiler" label="Boiler" />
                    <CheckboxField name="useAnyTechnology" label="Any" />
                  </HorizontalDiv>
                  <CheckboxField name="matchAllParts" label="Match All Parts" />
                  {!hidePartNumbersSelection && (
                    <>
                      <MultiSelectContainer>
                        <MultiSearchSelect
                          onChange={onPartIdValuesChange}
                          values={partIdValues}
                          options={partOptions}
                          name="parts"
                          placeholder="Select part numbers (Primary)"
                        />
                      </MultiSelectContainer>
                      <MultiSelectContainer>
                        <MultiSearchSelect
                          onChange={onSecondaryPartIdValuesChange}
                          values={secondaryPartIdValues}
                          options={partOptions}
                          name="secondaryParts"
                          placeholder="Select part numbers (Secondary)"
                        />
                      </MultiSelectContainer>
                    </>
                  )}
                </SectionContainer>
              )}
              <PrimaryProgressButton inProgress={formProps.isSubmitting} type="submit">
                Save
              </PrimaryProgressButton>
            </Form>
          );
        }}
      </Formik>
      {error && <FormErrorBox error={error} />}
    </>
  );
};

const SectionContainer = styled.div`
  margin-bottom: ${spacing02};
  padding: ${spacing02};
`;

const HorizontalDiv = styled.div`
  display: flex;
  gap: ${spacing01};
`;

const WarningText = styled.p`
  color: ${colourPrimaryOrange};
`;

const MultiSelectContainer = styled.div`
  margin-bottom: ${spacing02}'
`;
